/* FATCUTPRESS Visual — LIVING HALFTONE / REFERENCE
 * Independent WebGL adapter based on the approved HALFTONE LAB.
 */
(() => {
'use strict';

const canvas=document.getElementById('fatcut-bg-halftone');
const registry=window.FATCUT_VISUAL_REGISTRY;
if(!canvas||!registry)return;

const fatcutPointerInteractionEnabled=()=>document.body?.dataset.hasProducts!=='true';const gl=canvas.getContext('webgl',{alpha:false,antialias:false,depth:false,stencil:false,preserveDrawingBuffer:false});
if(!gl)return;
let visualActive=false;
let raf=0;

const vert=`
attribute vec2 a_position;
varying vec2 v_uv;
void main(){
  v_uv=a_position*0.5+0.5;
  gl_Position=vec4(a_position,0.0,1.0);
}`;

const simFrag=`
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_prev;
uniform vec2 u_texel;
uniform float u_decay;
uniform int u_count;
uniform vec3 u_points[10];

void main(){
  float c=texture2D(u_prev,v_uv).r;
  float n=texture2D(u_prev,v_uv+vec2(0.0,u_texel.y)).r;
  float s=texture2D(u_prev,v_uv-vec2(0.0,u_texel.y)).r;
  float e=texture2D(u_prev,v_uv+vec2(u_texel.x,0.0)).r;
  float w=texture2D(u_prev,v_uv-vec2(u_texel.x,0.0)).r;
  float ne=texture2D(u_prev,v_uv+u_texel).r;
  float nw=texture2D(u_prev,v_uv+vec2(-u_texel.x,u_texel.y)).r;
  float se=texture2D(u_prev,v_uv+vec2(u_texel.x,-u_texel.y)).r;
  float sw=texture2D(u_prev,v_uv-u_texel).r;

  float heat=c*0.28+(n+s+e+w)*0.12+(ne+nw+se+sw)*0.06;
  heat*=u_decay;

  for(int i=0;i<10;i++){
    if(i>=u_count)break;
    vec2 p=u_points[i].xy;
    float strength=u_points[i].z;
    vec2 d=v_uv-p;
    d.x*=u_texel.y/u_texel.x;
    float rr=dot(d,d);
    heat+=exp(-rr*1550.0)*strength;
  }

  gl_FragColor=vec4(clamp(heat,0.0,1.2),0.0,0.0,1.0);
}`;

const drawFrag=`
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_heat;
uniform vec2 u_resolution;
uniform float u_time;
uniform float u_grain;
uniform float u_brightness;

float hash21(vec2 p){
  p=fract(p*vec2(123.34,456.21));
  p+=dot(p,p+45.32);
  return fract(p.x*p.y);
}
float noise2(vec2 p){
  vec2 i=floor(p),f=fract(p);
  f=f*f*(3.0-2.0*f);
  return mix(mix(hash21(i),hash21(i+vec2(1,0)),f.x),
             mix(hash21(i+vec2(0,1)),hash21(i+vec2(1,1)),f.x),f.y);
}
float fbm(vec2 p){
  float v=0.0,a=0.5;
  for(int i=0;i<5;i++){
    v+=a*noise2(p);
    p=p*2.03+17.13;
    a*=0.5;
  }
  return v;
}
float heatAt(vec2 uv){
  return texture2D(u_heat,clamp(uv,vec2(0.001),vec2(0.999))).r;
}

void main(){
  vec2 uv=v_uv;
  float aspect=u_resolution.x/max(u_resolution.y,1.0);
  vec2 p=uv-0.5;
  p.x*=aspect;

  float heat=heatAt(uv);
  float field=fbm(vec2(p.x*1.45-u_time*0.035,p.y*1.45+u_time*0.021));
  field+=0.42*fbm(vec2(p.x*3.4+u_time*0.018,p.y*3.4-u_time*0.026));
  field+=heat*0.72;
  field=clamp(field/1.42,0.0,1.0);

  float portrait=1.0-smoothstep(0.72,1.0,aspect);
  float cellPx=mix(12.0,17.0,portrait);
  vec2 cell=vec2(cellPx/u_resolution.x,cellPx/u_resolution.y);
  vec2 id=floor(uv/cell);
  vec2 f=fract(uv/cell)-0.5;
  f.x*=u_resolution.x/u_resolution.y;

  float seed=hash21(id);
  float phase=seed*6.28318+id.x*0.17+id.y*0.11;
  float breath=sin(u_time*0.78+phase)*0.5+0.5;

  float directionTime=u_time*0.020;
  vec2 directionA=vec2(cos(directionTime),sin(directionTime));
  vec2 directionB=vec2(cos(-directionTime*0.73+2.10),sin(-directionTime*0.73+2.10));
  vec2 directionC=vec2(cos(directionTime*0.41+4.35),sin(directionTime*0.41+4.35));
  float waveA=sin(dot(id,directionA)*0.38-u_time*0.92);
  float waveB=sin(dot(id,directionB)*0.27+u_time*0.58);
  float waveC=sin(dot(id,directionC)*0.17-u_time*0.31);
  float travellingWave=0.5+0.5*(waveA*0.52+waveB*0.31+waveC*0.17);
  travellingWave=smoothstep(0.15,0.85,travellingWave);

  float sx=1.0+(seed-0.5)*0.10+0.025*sin(u_time*0.43+phase);
  float sy=1.0-(seed-0.5)*0.08-0.018*sin(u_time*0.39+phase);
  vec2 inkF=vec2(f.x/sx,f.y/sy);
  inkF+=0.010*vec2(
    sin(f.y*18.0+phase),
    cos(f.x*17.0-phase)
  );

  float local=field+(seed-0.5)*0.085;
  float idlePulse=mix(-0.015,0.022,breath);
  float wavePulse=mix(-0.050,0.092,travellingWave);
  float radius=mix(0.055,0.54,smoothstep(0.14,0.92,local))+idlePulse+wavePulse;

  float bleed=0.074+0.018*breath+0.035*travellingWave;
  float dist=length(inkF);
  float dotMask=1.0-smoothstep(radius,radius+bleed,dist);
  float softInk=1.0-smoothstep(radius+bleed*0.15,radius+bleed*1.85,dist);
  dotMask=max(dotMask,softInk*(0.055+0.060*breath));

  vec3 paper=vec3(0.972,0.962,0.946);
  vec3 ink=vec3(0.18,0.17,0.16);
  vec3 mid=vec3(0.61,0.58,0.54);
  vec3 col=mix(paper,mix(mid,ink,smoothstep(0.30,0.90,local)),dotMask);

  float grain=(hash21(gl_FragCoord.xy+floor(u_time*7.0))-0.5)*0.020*u_grain;
  col+=grain;

  float vignette=1.0-smoothstep(0.30,0.92,length(p));
  col*=0.90+0.10*vignette;
  col*=u_brightness;

  gl_FragColor=vec4(col,1.0);
}`;

function shader(type,src){
  const s=gl.createShader(type);
  gl.shaderSource(s,src);
  gl.compileShader(s);
  if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(s));
  return s;
}
function program(vs,fs){
  const p=gl.createProgram();
  gl.attachShader(p,shader(gl.VERTEX_SHADER,vs));
  gl.attachShader(p,shader(gl.FRAGMENT_SHADER,fs));
  gl.linkProgram(p);
  if(!gl.getProgramParameter(p,gl.LINK_STATUS))throw new Error(gl.getProgramInfoLog(p));
  return p;
}

const simProgram=program(vert,simFrag);
const drawProgram=program(vert,drawFrag);

const quad=gl.createBuffer();
gl.bindBuffer(gl.ARRAY_BUFFER,quad);
gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);

const SIM_SIZE=256;
function makeTexture(){
  const t=gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D,t);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,SIM_SIZE,SIM_SIZE,0,gl.RGBA,gl.UNSIGNED_BYTE,null);
  return t;
}
function makeFbo(texture){
  const f=gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER,f);
  gl.framebufferTexture2D(gl.FRAMEBUFFER,gl.COLOR_ATTACHMENT0,gl.TEXTURE_2D,texture,0);
  return f;
}
let texA=makeTexture(),texB=makeTexture();
let fboA=makeFbo(texA),fboB=makeFbo(texB);
gl.bindFramebuffer(gl.FRAMEBUFFER,null);

const points=[];
const active=new Map();

function addPoint(x,y,strength=.9){
  points.push({x,y:1-y,strength,born:performance.now()});
  if(points.length>120)points.splice(0,points.length-120);
}
function posFromEvent(e){
  const rr=canvas.getBoundingClientRect();
  return {
    x:Math.max(0,Math.min(1,(e.clientX-rr.left)/rr.width)),
    y:Math.max(0,Math.min(1,(e.clientY-rr.top)/rr.height))
  };
}
window.addEventListener('pointerdown',e=>{if(!fatcutPointerInteractionEnabled())return;
  const p=posFromEvent(e);
  active.set(e.pointerId,p);
  addPoint(p.x,p.y,.24);
},{passive:true});
window.addEventListener('pointermove',e=>{if(!fatcutPointerInteractionEnabled())return;
  const p=posFromEvent(e);
  const prev=active.get(e.pointerId)||p;
  const dx=p.x-prev.x,dy=p.y-prev.y;
  const dist=Math.hypot(dx,dy);
  const steps=Math.max(1,Math.ceil(dist*150));
  const strength=(e.pointerType==='mouse'&&e.buttons===0?.135:.27);
  for(let i=1;i<=steps;i++){
    addPoint(prev.x+dx*i/steps,prev.y+dy*i/steps,strength);
  }
  active.set(e.pointerId,p);
},{passive:true});
window.addEventListener('pointerenter',e=>{
  if(e.pointerType!=='mouse')return;
  const p=posFromEvent(e);
  active.set(e.pointerId,p);
  addPoint(p.x,p.y,.11);
},{passive:true});
const end=e=>active.delete(e.pointerId);
window.addEventListener('pointerup',end,{passive:true});
window.addEventListener('pointercancel',end,{passive:true});

function resize(){
  const dpr=Math.min(devicePixelRatio||1,2);
  const w=Math.max(1,Math.floor(innerWidth*dpr));
  const h=Math.max(1,Math.floor(innerHeight*dpr));
  if(canvas.width!==w||canvas.height!==h){
    canvas.width=w;
    canvas.height=h;
  }
}
addEventListener('resize',resize,{passive:true});
resize();

function bindQuad(p){
  const loc=gl.getAttribLocation(p,'a_position');
  gl.bindBuffer(gl.ARRAY_BUFFER,quad);
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc,2,gl.FLOAT,false,0,0);
}

let last=performance.now();
function frame(now){
  resize();
  const dt=Math.min(32,now-last);
  last=now;

  const idx=points.findIndex(p=>now-p.born<190);
  points.splice(0,idx<0?points.length:idx);
  const batch=points.slice(-10);

  gl.useProgram(simProgram);
  bindQuad(simProgram);
  gl.viewport(0,0,SIM_SIZE,SIM_SIZE);
  gl.bindFramebuffer(gl.FRAMEBUFFER,fboB);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D,texA);
  gl.uniform1i(gl.getUniformLocation(simProgram,'u_prev'),0);
  gl.uniform2f(gl.getUniformLocation(simProgram,'u_texel'),1/SIM_SIZE,1/SIM_SIZE);
  gl.uniform1f(gl.getUniformLocation(simProgram,'u_decay'),Math.pow(0.9885,dt/16.67));
  gl.uniform1i(gl.getUniformLocation(simProgram,'u_count'),batch.length);
  const arr=new Float32Array(30);
  for(let i=0;i<batch.length;i++){
    arr[i*3]=batch[i].x;
    arr[i*3+1]=batch[i].y;
    arr[i*3+2]=batch[i].strength;
  }
  gl.uniform3fv(gl.getUniformLocation(simProgram,'u_points[0]'),arr);
  gl.drawArrays(gl.TRIANGLES,0,6);

  [texA,texB]=[texB,texA];
  [fboA,fboB]=[fboB,fboA];

  gl.useProgram(drawProgram);
  bindQuad(drawProgram);
  gl.viewport(0,0,canvas.width,canvas.height);
  gl.bindFramebuffer(gl.FRAMEBUFFER,null);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D,texA);
  gl.uniform1i(gl.getUniformLocation(drawProgram,'u_heat'),0);
  gl.uniform2f(gl.getUniformLocation(drawProgram,'u_resolution'),canvas.width,canvas.height);
  gl.uniform1f(gl.getUniformLocation(drawProgram,'u_time'),now*.001);
  gl.uniform1f(gl.getUniformLocation(drawProgram,'u_grain'),1.0);
  gl.uniform1f(gl.getUniformLocation(drawProgram,'u_brightness'),1.0);
  gl.drawArrays(gl.TRIANGLES,0,6);

  if(visualActive)raf=requestAnimationFrame(frame);
}
const adapter={
  activate(){
    visualActive=true;
    canvas.style.opacity='1';
    if(!raf)raf=requestAnimationFrame(frame);
  },
  deactivate(){
    canvas.style.opacity='0';
    window.setTimeout(()=>{
      visualActive=false;
      if(raf)cancelAnimationFrame(raf);
      raf=0;
    },950);
  },
  update(){}
};
registry.add('living_halftone',adapter);
registry.flush();
})();